/**
 * V108-6-2 — onboarding step 4, "Grant": every source a customer can connect today, ordered by value per
 * effort (the ranked setup steps first — most objectives unlocked for the least time — then the rest by
 * effort), one action each, with the environment picked at grant (production by default; staging shows as
 * a preview and doesn't count, ADR-0085 §5). The connect form is the same drawer Setup uses.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select } from '@aigovops/design-system';
import { connectCodeFor, creatableKinds, getConnectorManifest, getEnablerCatalogEntry } from '@aigovops/core';
import type { ConnectorRow } from '../../lib/api';
import { listConnectors } from '../../lib/api';
import { setConnectorEnvironment, type EnablerItem } from '../../lib/setup-api';
import { isPostureFormKind, PostureConnections } from '../PostureConnections';
import { ActorKind, State, UnlockChips } from './parts';

export interface GrantRow { kind: string; label: string; group: string; effort: number; step: EnablerItem | null; privileged: boolean; connected: ConnectorRow | null }

const ENV_KINDS_EXEMPT = new Set(['github', 'gitlab', 'sbom', 'local_upload', 'vuln_scanner']);
export const ENVIRONMENTS = [['production', 'Production'], ['staging', 'Staging'], ['development', 'Development']] as const;

/** PURE: the Grant list. Ranked setup steps first (value per effort), then the other creatable kinds by effort. */
export function grantRows(enablers: readonly EnablerItem[], connectors: readonly ConnectorRow[]): GrantRow[] {
  const byKind = new Map<string, EnablerItem>();
  for (const e of enablers) {
    const kind = e.progress.kind ?? (e.code.startsWith('connect:') ? e.code.slice(8) : null);
    if (kind && e.actor === 'admin_console' && e.code === connectCodeFor(kind)) byKind.set(kind, e);
  }
  const rows = creatableKinds().map((kind): GrantRow => {
    const m = getConnectorManifest(kind)!;
    const entry = getEnablerCatalogEntry(connectCodeFor(kind));
    return {
      kind, label: m.ui.label, group: m.ui.group, effort: entry?.effort_min ?? 15, step: byKind.get(kind) ?? null,
      privileged: !!m.privilege, connected: connectors.find((c) => c.kind === kind && c.status === 'active') ?? null,
    };
  });
  const rank = (r: GrantRow) => (r.step && r.step.rank != null && r.step.state !== 'done' ? r.step.rank : 10_000);
  return rows.sort((a, b) => rank(a) - rank(b) || Number(a.privileged) - Number(b.privileged) || a.effort - b.effort || a.label.localeCompare(b.label));
}

export function GrantSources({ slug, enablers, connectors, editable, github, onChanged, toast }: {
  slug: string; enablers: readonly EnablerItem[]; connectors: readonly ConnectorRow[]; editable: boolean;
  /** The GitHub row's own block (Connect, the repository pick, the connected repositories), rendered inline. */
  github: ReactNode;
  onChanged: () => Promise<unknown>; toast: (m: string) => void;
}) {
  const rows = useMemo(() => grantRows(enablers, connectors), [enablers, connectors]);
  const [env, setEnv] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const before = useRef<Set<string>>(new Set());
  const opener = useRef<HTMLElement | null>(null);
  const top = rows.filter((r) => r.step || r.connected || r.kind === 'github');
  const rest = rows.filter((r) => !top.includes(r));

  const start = (kind: string, el: HTMLElement) => {
    opener.current = el;
    before.current = new Set(connectors.filter((c) => c.kind === kind).map((c) => c.connector_id));
    setOpen(kind);
  };
  // When the drawer closes: label a connection made in it with the environment picked at grant.
  const closed = async () => {
    const kind = open;
    setOpen(null);
    if (!kind) return;
    const picked = env[kind] ?? 'production';
    const fresh = (await listConnectors(slug)).connectors.filter((c) => c.kind === kind && !before.current.has(c.connector_id));
    if (fresh.length && picked !== 'production') {
      for (const c of fresh) await setConnectorEnvironment(slug, c.connector_id, picked);
      toast(`Connected as ${picked}. It shows as a preview and doesn't count until a program includes ${picked}.`);
    } else if (fresh.length) toast('Connected. It shows as done after our first read.');
    await onChanged();
  };

  const row = (r: GrantRow) => (
    <li key={r.kind} data-grant-kind={r.kind}>
      <div>
        <strong>{r.label}</strong>
        <div className="su-row-meta">
          {r.step && <UnlockChips step={r.step} />}
          {r.privileged && <span className="su-chip" data-kind="privilege">Beyond read-only</span>}
          {r.step ? <ActorKind step={r.step} /> : <span className="su-kind">{r.group} · {r.effort} min</span>}
        </div>
      </div>
      <div className="su-grant-act">
        {r.step?.progress.state === 'failed' ? <Link className="su-btn" to={`/w/${slug}/app/setup?step=${r.step.id}#steps`}><State tone="attention" glyph="alert">Needs attention</State><span className="su-sr">: {r.label}</span></Link>
          : r.step?.progress.state === 'waiting' ? <State tone="progress" glyph="clock">Waiting for our first read</State>
          : r.connected ? <State tone="done" glyph="check">Connected</State>
          : r.kind === 'github' ? null
          : !editable ? <span className="su-hint">An owner or admin connects this.</span>
          : isPostureFormKind(r.kind) ? <>
            {!ENV_KINDS_EXEMPT.has(r.kind) && <label className="su-hint" style={{ display: 'grid', gap: 2 }}>
              <span>Environment</span>
              <Select aria-label={`Environment for ${r.label}`} value={env[r.kind] ?? 'production'} onChange={(e) => setEnv((x) => ({ ...x, [r.kind]: e.target.value }))}>
                {ENVIRONMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </label>}
            <Button onClick={(e) => start(r.kind, e.currentTarget)}>Connect<span className="su-sr"> {r.label}</span></Button>
          </>
          : <Link className="su-btn" to={`/w/${slug}/app/setup#manage-sources`}>Set up in Setup<span className="su-sr">: {r.label}</span></Link>}
      </div>
      {/* V108-8-1: the GitHub block also carries the repository pick, Choose repositories and Connect another account. */}
      {r.kind === 'github' && <div style={{ gridColumn: '1 / -1' }}>{github}</div>}
    </li>
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
      <ul className="su-grant" aria-label="Sources, most useful first">{top.map(row)}</ul>
      {rest.length > 0 && <details className="su-more" open={showAll} onToggle={(e) => setShowAll((e.currentTarget as HTMLDetailsElement).open)}>
        <summary>More sources ({rest.length})</summary>
        <ul className="su-grant" aria-label="More sources">{rest.map(row)}</ul>
      </details>}
      {open && <PostureConnections slug={slug} editable={editable} connectKind={open} drawerOnly onDrawerClosed={() => void closed()} returnFocusTo={() => opener.current} />}
    </div>
  );
}
